/* eslint react-hooks/exhaustive-deps: "warn" */
import { Fragment, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './ManufacturingBand.module.scss';

type Translate = (key: string) => string;

// Each clause is translated as a whole phrase, then word-split for the animation —
// never translate individual words, or the translation loses coherence.
const getHeadingWords = (t: Translate): { text: string; accent: boolean }[] => [
  ...t('The supplier record and the filing it came from')
    .split(' ')
    .map((text) => ({ text, accent: false })),
  { text: t('arrive in the same response.'), accent: true },
];

const getChips = (t: Translate) => [t('Registry of record'), t('Filing reference'), t('Date retrieved')];

export const ManufacturingBand = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.35 });

  return (
    <Section background="linear-gradient(135deg, #042354 0%, #081D3D 100%)" className={styles.root}>
      <div ref={ref} className={styles.inner}>
        <TitleBlock
          align="left"
          theme="dark"
          accent="none"
          className={cn(styles.titleBlock, revealed && styles.headingIn)}
          descriptionClassName={cn(styles.sub, revealed && styles.subIn)}
          title={getHeadingWords(t).map((word, i) => (
            <span
              key={i}
              className={cn(styles.word, word.accent && styles.accent)}
              style={{ transitionDelay: `${i * 45}ms` }}
            >
              {word.text}
            </span>
          ))}
          description={t(
            'Aggregators hand you a company profile and ask you to trust it. Every field we return carries the registry it was taken from and the date it was filed, so a procurement decision can be reconstructed years later by someone who was not in the room.',
          )}
        />

        <div className={cn(styles.chips, revealed && styles.chipsIn)}>
          {getChips(t).map((chip, i) => (
            <Fragment key={chip}>
              {i > 0 && <span className={styles.chipSep} />}
              <span className={styles.chip}>{chip}</span>
            </Fragment>
          ))}
        </div>
      </div>
    </Section>
  );
};
